import * as THREE from "three";
import { asOverheadUI } from "./overheadLayer.js";

/**
 * A magic teleport portal styled after the Diablo II town portal: a tall, luminous oval of
 * electric-BLUE energy with concentric rings spiralling inward around a white-hot core. It's
 * frame-animated (pre-rendered frames cycled, not just a spinning image), deliberately
 * low-res for a chunky pixelated look (NearestFilter). Opens for a fixed lifetime (default
 * 60s), shows a floating name + live countdown, can be CLICK-selected like an NPC, and warps
 * the player to its destination on T. Purely client-side scene content over the zone system.
 */

const FW = 30, FH = 50, FRAMES = 12; // chunky per-frame cell (tall oval) + frame count
const FPS = 12;
const APPEAR = 0.5;                   // seconds to grow open from the middle
const BODY_W = 0.93, BODY_H = 1.55;  // full portal sprite size (world units)
const HALO_W = 1.4, HALO_H = 1.7;    // full bloom-halo size

/** Render the FRAMES-long portal animation strip into one horizontal sheet canvas: a tall
 *  blue oval whose concentric rings spiral inward (the D2 town-portal vortex), per frame. */
function makePortalSheet(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = FW * FRAMES; c.height = FH;
  const ctx = c.getContext("2d")!;
  const sheet = ctx.createImageData(c.width, FH);
  const d = sheet.data;
  const cx = FW / 2, cy = FH / 2, rx = FW * 0.40, ry = FH * 0.47;
  for (let f = 0; f < FRAMES; f++) {
    const phase = (f / FRAMES) * Math.PI * 2;
    const ox = f * FW;
    for (let y = 0; y < FH; y++) {
      for (let x = 0; x < FW; x++) {
        const nx = (x - cx) / rx, ny = (y - cy) / ry;
        const er = Math.hypot(nx, ny);          // 0 centre … 1 ellipse edge
        const i = (y * c.width + (ox + x)) * 4;
        if (er > 1.04) { d[i + 3] = 0; continue; }
        const a = Math.atan2(ny, nx);
        // concentric rings spiralling INWARD (phase scans the rings toward the core)
        let rings = Math.sin(er * 8 - phase * 2 + a * 2);
        rings = 0.5 + 0.5 * rings;
        const beam = Math.exp(-Math.pow(nx / 0.34, 2)) * (1 - Math.abs(ny) * 0.25); // vertical light shaft
        const rim = Math.exp(-Math.pow((er - 0.93) / 0.1, 2));   // bright outer ring
        const core = Math.max(0, 0.26 - er) / 0.26;              // white-hot eye
        let lum = rings * (1 - er) * 0.7 + rim * 0.9 + beam * 0.45 + core;
        lum = Math.min(1, lum);
        const t = lum, t15 = Math.pow(lum, 1.6);
        d[i] = Math.round(20 + 225 * t15);        // R  (deep blue → white-hot)
        d[i + 1] = Math.round(85 + 170 * t);      // G  (cyan lift)
        d[i + 2] = Math.round(165 + 90 * t);      // B  (stays blue)
        const edge = er > 0.9 ? Math.max(0, 1 - (er - 0.9) / 0.14) : 1; // soft rim falloff
        d[i + 3] = Math.round(Math.min(255, (75 + 200 * t) * edge));
      }
    }
  }
  ctx.putImageData(sheet, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.x = 1 / FRAMES; // show one frame; offset.x scans the strip per tick
  return tex;
}

/** A soft radial glow used as an additive bloom halo behind the portal (electric blue). */
function makeHalo(): THREE.CanvasTexture {
  const s = 64, c = document.createElement("canvas"); c.width = c.height = s;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  g.addColorStop(0, "rgba(120,200,255,0.6)");
  g.addColorStop(0.5, "rgba(50,120,235,0.28)");
  g.addColorStop(1, "rgba(20,50,140,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A floating "Name  m:ss  [T]" label texture (the [T] chip appears only when selected). */
function renderPortalLabel(name: string, time: string, selected: boolean): THREE.CanvasTexture {
  const fs = 26, pad = 8, gap = 12, chip = fs + 6;
  const m = document.createElement("canvas").getContext("2d")!;
  m.font = `${fs}px monospace`;
  const nameW = Math.ceil(m.measureText(name).width);
  const timeW = Math.ceil(m.measureText(time).width);
  const tChip = selected ? gap + chip : 0;
  const w = pad + nameW + gap + timeW + tChip + pad, h = chip + pad * 2;
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.font = `${fs}px monospace`; ctx.textBaseline = "middle";
  ctx.fillStyle = "#d9b8ff"; ctx.fillText(name, pad, h / 2);
  ctx.fillStyle = "#8be9ff"; ctx.fillText(time, pad + nameW + gap, h / 2);
  if (selected) {
    const kx = pad + nameW + gap + timeW + gap, ky = (h - chip) / 2;
    ctx.fillStyle = "rgba(12,12,16,.92)"; ctx.strokeStyle = "#c89aff"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(kx, ky, chip, chip, 5); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#c89aff"; ctx.textAlign = "center";
    ctx.fillText("T", kx + chip / 2, h / 2 + 1); ctx.textAlign = "left";
  }
  const tex = new THREE.CanvasTexture(c);
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

export class PortalEntity {
  readonly group = new THREE.Group();
  private readonly sheet: THREE.CanvasTexture;
  private readonly mat: THREE.SpriteMaterial;
  private readonly halo: THREE.Sprite;
  private readonly portalSprite: THREE.Sprite;
  private readonly haloTex: THREE.CanvasTexture;
  private readonly light: THREE.PointLight;
  private readonly label: THREE.Sprite;
  private frame = 0;
  private clock = 0;
  private age = 0;
  private lastSec = -1;
  private selected = false;
  remaining: number;

  constructor(
    readonly name: string,
    /** Zone the portal physically exists in (only shown/clickable there). */
    readonly zone: string,
    readonly destZone: string,
    readonly destSpawn: { x: number; y: number },
    x: number, z: number,            // world units
    readonly lifetime = 60,
  ) {
    this.remaining = lifetime;
    // additive bloom halo behind the portal
    this.haloTex = makeHalo();
    this.halo = new THREE.Sprite(new THREE.SpriteMaterial({
      map: this.haloTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    }));
    this.halo.scale.set(0, 0, 1); this.halo.position.y = 0.8; this.group.add(this.halo); // grows in on tick
    // the frame-animated portal itself (standing vertical oval)
    this.sheet = makePortalSheet();
    this.mat = new THREE.SpriteMaterial({ map: this.sheet, transparent: true, depthWrite: false });
    this.portalSprite = new THREE.Sprite(this.mat);
    this.portalSprite.scale.set(0, 0, 1); this.portalSprite.position.y = 0.82; // grows in on tick
    this.group.add(this.portalSprite);
    this.light = new THREE.PointLight(0x4aa6ff, 7, 10, 2);
    this.light.position.set(0, 0.85, 0); this.group.add(this.light);
    this.label = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, depthTest: false }));
    this.label.position.set(0, 1.95, 0); asOverheadUI(this.label); this.group.add(this.label);
    this.group.position.set(x, 0, z);
    this.refreshLabel();
  }

  setSelected(on: boolean): void {
    if (this.selected === on) return;
    this.selected = on; this.lastSec = -1; this.refreshLabel();
  }

  /** Resync the countdown from the authoritative server value. */
  setRemaining(seconds: number): void {
    this.remaining = seconds;
  }

  /** Advance the animation frame + pulse + the (display-only) countdown. The SERVER owns the
   *  real lifetime + removal; this just animates and shows the time left. */
  tick(dt: number): void {
    this.remaining -= dt; this.clock += dt; this.age += dt;
    this.frame = (this.frame + dt * FPS) % FRAMES;
    this.sheet.offset.x = Math.floor(this.frame) / FRAMES;
    // Smoothly grow open from the middle (ease-out) over APPEAR seconds.
    const open = 1 - Math.pow(1 - Math.min(1, this.age / APPEAR), 3);
    const pulse = 1 + Math.sin(this.clock * 4) * 0.06;
    this.portalSprite.scale.set(BODY_W * open, BODY_H * open, 1);
    this.halo.scale.set(HALO_W * open * pulse, HALO_H * open * pulse, 1);
    this.light.intensity = (5 + Math.sin(this.clock * 5) * 1.6) * open;
    const sec = Math.max(0, Math.ceil(this.remaining));
    if (sec !== this.lastSec) { this.lastSec = sec; this.refreshLabel(); }
  }

  /** Did a click ray hit the portal (its body or glow halo)? */
  pick(ray: THREE.Raycaster): boolean {
    return ray.intersectObjects([this.portalSprite, this.halo], false).length > 0;
  }

  private refreshLabel(): void {
    const sec = Math.max(0, Math.ceil(this.remaining));
    const time = `0:${String(sec).padStart(2, "0")}`;
    const mat = this.label.material as THREE.SpriteMaterial;
    mat.map?.dispose();
    const tex = renderPortalLabel(this.name, time, this.selected);
    mat.map = tex; mat.needsUpdate = true;
    const w = tex.image.width, h = tex.image.height;
    this.label.scale.set((w / h) * 0.6, 0.6, 1);
  }

  dispose(): void {
    this.sheet.dispose(); this.mat.dispose();
    this.haloTex.dispose();
    (this.halo.material as THREE.SpriteMaterial).dispose();
    const lm = this.label.material as THREE.SpriteMaterial;
    lm.map?.dispose(); lm.dispose();
  }
}
